import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { loginWithPassword, sendEmailCode, verifyEmailCode } from '@/auth/admin-auth';
import { isReadOnlyAdminMode } from '@/lib/admin-mode';

interface LocationState {
  from?: { pathname?: string };
}

type Mode = 'password' | 'code';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const readOnly = isReadOnlyAdminMode();
  const from =
    (location.state as LocationState | null)?.from?.pathname ?? '/acquisition';

  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function finish(action: () => Promise<void>) {
    setError(null);
    setMessage(null);
    setSubmitting(true);
    try {
      await action();
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (mode === 'password') {
      await finish(() => loginWithPassword(email, password));
      return;
    }
    if (!codeSent) {
      setError(null);
      setMessage(null);
      setSubmitting(true);
      try {
        await sendEmailCode(email);
        setCodeSent(true);
        setMessage('Check your Fantopy email for the one-time code, then enter it here.');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not send email code');
      } finally {
        setSubmitting(false);
      }
      return;
    }
    await finish(() => verifyEmailCode(email, code));
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="font-barlow uppercase tracking-wide">Fantopy Admin</CardTitle>
          <CardDescription>
            {readOnly
              ? 'Production read-only mode. Sign in with your Fantopy email identity to inspect live data.'
              : 'Sign in with your Fantopy email identity. The backend verifies your account before admin data loads.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-1 text-sm">
              <button
                type="button"
                className={`rounded-md px-3 py-2 ${mode === 'password' ? 'bg-background shadow' : ''}`}
                onClick={() => {
                  setMode('password');
                  setError(null);
                  setMessage(null);
                }}
              >
                Password
              </button>
              <button
                type="button"
                className={`rounded-md px-3 py-2 ${mode === 'code' ? 'bg-background shadow' : ''}`}
                onClick={() => {
                  setMode('code');
                  setError(null);
                  setMessage(null);
                }}
              >
                Email code
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="admin-email">
                Email
              </label>
              <Input
                id="admin-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            {mode === 'password' ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="admin-password">
                  Password
                </label>
                <Input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            ) : codeSent ? (
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="admin-code">
                  Email code
                </label>
                <Input
                  id="admin-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </div>
            ) : null}

            {message ? <p className="text-sm text-muted-foreground">{message}</p> : null}
            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
            <Button type="submit" disabled={submitting} className="w-full">
              {submitting
                ? 'Checking...'
                : mode === 'password'
                  ? 'Sign in and verify admin access'
                  : codeSent
                    ? 'Verify code and continue'
                    : 'Send email code'}
            </Button>
            <p className="text-xs text-muted-foreground">
              Owner access is controlled server-side by Supabase token verification plus the admin allowlist. Agents never receive owner passwords or email codes.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
